import { Box, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import styles from './pinCodeInput.module.scss';
import PropTypes from 'prop-types';
import { validateInput, createPinArray } from './utils';
import clsx from 'clsx';

const PinCodeInput = ({ length = 4, onComplete, value = '', error, onChange, label, disabled }) => {
    const inputsRef = useRef([]);
    const [_value, setValues] = useState(createPinArray(length, value));
    const [focusedIndex, setFocusedIndex] = useState(-1);

    useEffect(() => {
        if (length !== _value.length) {
            setValues(createPinArray(length, _value.join('')));
        }
    }, [length]);

    useEffect(() => {
        if (value !== _value.join('')) {
            setValues(createPinArray(length, value));
            setTimeout(() => {
                focusNextField(value.length - 1);
            });
        }
    }, [value]);

    const handleComplete = (values) => {
        const code = values.join('');

        if (code.length === length) {
            onComplete?.(code);
        }
    };

    const focusNextField = (index, event) => {
        const nextIndex = index + 1;
        const canFocusNext = nextIndex < (length ?? 0);
        if (canFocusNext) {
            event?.preventDefault();
            inputsRef.current[nextIndex].focus();
            setFocusedIndex(nextIndex);
        }
    };

    const focusPrevField = (index, event) => {
        const nextIndex = index - 1;
        const canFocusNext = nextIndex > -1;
        if (canFocusNext) {
            event?.preventDefault();
            inputsRef.current[nextIndex].focus();
            setFocusedIndex(nextIndex);
        }
    };

    const setFieldValue = (val, index) => {
        const values = [..._value];
        values[index] = val;
        setValues(values);

        onChange?.(values.join(''));

        if (index === length - 1 && val !== '') {
            handleComplete(values);
        }
    };

    const handleChange = (event, index) => {
        const inputValue = event.target.value;
        const nextCharOrValue =
            inputValue.length === 2 ? inputValue.split('')[inputValue.length - 1] : inputValue;

        const isValid = validateInput(nextCharOrValue);

        if (nextCharOrValue.length < 2) {
            if (isValid) {
                setFieldValue(nextCharOrValue, index);
                focusNextField(index);
            } else {
                setFieldValue('', index);
            }
        } else if (isValid) {
            setValues(createPinArray(length ?? 0, inputValue));
        }
    };

    const handleKeyDown = (event, index) => {
        const { ctrlKey, metaKey, key, shiftKey, target } = event;
        const inputValue = target.value;

        const canTypeSign =
            key === 'Backspace' ||
            key === 'Tab' ||
            key === 'Control' ||
            key === 'Delete' ||
            (ctrlKey && key === 'v') ||
            (metaKey && key === 'v')
                ? true
                : !Number.isNaN(Number(key));

        if (!canTypeSign) {
            event.preventDefault();
        }

        if (key === 'ArrowLeft' || (shiftKey && key === 'Tab')) {
            focusPrevField(index, event);
        } else if (key === 'ArrowRight' || key === 'Tab' || key === ' ') {
            focusNextField(index, event);
        } else if (key === 'Delete') {
            setFieldValue('', index);
        } else if (key === 'Backspace') {
            if (index !== 0) {
                setFieldValue('', index);
                if (length === index + 1) {
                    if (event.target.value === '') {
                        focusPrevField(index, event);
                    }
                } else {
                    focusPrevField(index, event);
                }
            }
        } else if (inputValue.length > 0 && key === _value[index]) {
            focusNextField(index, event);
        }
    };

    const handleFocus = (event, index) => {
        event.target.select();
        setFocusedIndex(index);
    };

    const handleBlur = () => {
        setFocusedIndex(-1);
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const copyValue = event.clipboardData.getData('text/plain').replace(/[\n\r\s]+/g, '');
        const isValid = validateInput(copyValue.trim());

        if (isValid) {
            const copyValueToPinArray = createPinArray(length, copyValue);
            setValues(copyValueToPinArray);
            focusNextField(copyValueToPinArray.length - 2);
            onChange?.(copyValueToPinArray);

            if (copyValueToPinArray.join('').length === length) {
                handleComplete(copyValueToPinArray);
            }
        }
    };

    return (
        <Box>
            {label && <Typography margin={'1rem 0'}>{label}</Typography>}
            <Box className={styles.inputsWrapper}>
                {_value.map((code, index) => (
                    <input
                        ref={(node) => {
                            inputsRef.current[index] = node;
                        }}
                        disabled={disabled}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{1}"
                        placeholder={focusedIndex === index ? '_' : undefined}
                        key={index}
                        className={clsx(styles.input, !!error && styles.inputWithError)}
                        value={code}
                        onChange={(event) => handleChange(event, index)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        onFocus={(event) => handleFocus(event, index)}
                        onBlur={handleBlur}
                        onPaste={handlePaste}
                    />
                ))}
            </Box>
            {error && <Typography color="#ff2d55">{error}</Typography>}
        </Box>
    );
};

PinCodeInput.propTypes = {
    length: PropTypes.number,
    onComplete: PropTypes.func,
    onChange: PropTypes.func,
    error: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.string
};

export default PinCodeInput;
